import React, { useEffect, useState } from "react";
import { getClasses } from "../../utils/getClasses";
import { toast } from "react-toastify";
import { importStudents } from "../../utils/importStudents";
import Papa from "papaparse";
import * as XLSX from "xlsx";

interface Student {
  firstName: string;
  lastName: string;
  email: string;
}

interface ImportStudentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (result: {
    message?: string;
    importedEmails: string[];
    newImportedStudents: Array<Student & { temporaryPassword: string }>;
    failedEmails: { email: string; reason: string }[];
    error?: string;
  }) => void;
  selectedClassId?: string;
}

async function getClassById(classId: string) {
  const { classes, error } = await getClasses();
  if (error) return { error };
  const cls = classes.find((c: any) => c.id === classId);
  if (!cls) return { error: "Class not found" };
  return { cls };
}

// K-5: only the first name is required. Last name and email are optional
// (a student without an email logs in with a picture PIN instead).
function rowsToStudents(rows: any[]): Student[] {
  return rows
    .map((row) => ({
      firstName: String(
        row.firstName ?? row["First Name"] ?? row["First name"] ?? row["first name"] ?? row.first_name ?? ""
      ).trim(),
      lastName: String(
        row.lastName ?? row["Last Name"] ?? row["Last name"] ?? row["last name"] ?? row.last_name ?? ""
      ).trim(),
      email: String(
        row.email ?? row.Email ?? row["Email Address"] ?? row["email address"] ?? ""
      ).trim(),
    }))
    .filter((student) => student.firstName !== "");
}

export default function ImportStudentsModal({
  isOpen,
  onClose,
  onComplete,
  selectedClassId,
}: ImportStudentsModalProps) {
  const [students, setStudents] = useState<Student[]>([
    { firstName: "", lastName: "", email: "" },
  ]);
  const [selectedMethod, setSelectedMethod] = useState<"file" | "manual">(
    "file"
  );
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [classes, setClasses] = useState<any[]>([]);
  const [singleClass, setSingleClass] = useState<any | null>(null);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [loadingClass, setLoadingClass] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setStudents([{ firstName: "", lastName: "", email: "" }]);
      setSelectedMethod("file");
      setFile(null);
      setUploading(false);
      setSingleClass(null);
      setClasses([]);
      setSelectedClass("");
    } else {
      if (selectedClassId) {
        setLoadingClass(true);
        getClassById(selectedClassId).then(({ cls, error }) => {
          if (error) {
            toast.error(error);
            setSingleClass(null);
          } else {
            setSingleClass(cls);
            setSelectedClass(cls.id);
          }
          setLoadingClass(false);
        });
      } else {
        loadClasses();
      }
    }
  }, [isOpen, selectedClassId]);

  async function extractStudentsFromFile(file: File): Promise<Student[]> {
    const extension = file.name.split(".").pop()?.toLowerCase();

    if (extension === "csv") {
      return await readStudentsFromCSV(file);
    } else if (["xls", "xlsx"].includes(extension || "")) {
      return await readStudentsFromExcel(file);
    }

    throw new Error("Unsupported file format");
  }

  function readStudentsFromCSV(file: File): Promise<Student[]> {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          const rows = results.data as any[];
          resolve(rowsToStudents(rows));
        },
        error: reject,
      });
    });
  }

  async function readStudentsFromExcel(file: File): Promise<Student[]> {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const data = XLSX.utils.sheet_to_json(sheet);

    return rowsToStudents(data as any[]);
  }

  async function loadClasses() {
    const { classes, error } = await getClasses();
    if (error) {
      toast.error(error);
    } else {
      setClasses(classes);
      setSelectedClass(classes[0]?.id || "");
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      setFile(file);
    }
  }

  async function handleImport() {
    const classId = selectedClass;
    if (!classId) {
      toast.error("Please select a class");
      return;
    }

    setUploading(true);

    if (selectedMethod === "file") {
      if (!file) {
        toast.error("Please upload a file");
        setUploading(false);
        return;
      }

      try {
        const students = await extractStudentsFromFile(file);

        if (students.length === 0) {
          toast.error("No valid students found in the file");
          setUploading(false);
          return;
        }

        const result = await importStudents({ students, classId });

        if (result.error) {
          toast.error(result.error);
          onClose();
          onComplete({
            importedEmails: [],
            newImportedStudents: [],
            failedEmails: [],
            error: result.error,
          });
        } else {
          toast.success(
            result.message || `Imported ${students.length} students`
          );
          onClose();
          onComplete(result);
        }
      } catch (err: any) {
        toast.error("Failed to process the file");
        console.error(err);
      } finally {
        setUploading(false);
      }
    } else {
      try {
        const validStudents = students.filter(
          (student) => student.firstName.trim() !== ""
        );

        if (validStudents.length === 0) {
          toast.error("Add at least one student");
          setUploading(false);
          return;
        }

        const result = await importStudents({
          students: validStudents,
          classId,
        });

        if (result.error) {
          toast.error(result.error);
          onClose();
          onComplete({
            importedEmails: [],
            newImportedStudents: [],
            failedEmails: [],
            error: result.error,
          });
        } else {
          toast.success(
            result.message || `Imported ${validStudents.length} students`
          );
          onClose();
          onComplete(result);
        }
      } catch (err: any) {
        toast.error("Failed to process students");
        console.error(err);
      } finally {
        setUploading(false);
      }
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center">
      <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            {selectedClassId && singleClass
              ? `Import Students to ${singleClass.name}`
              : "Import Students"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            ✕
          </button>
        </div>

        <div className="p-6">
          {/* Import Methods */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-1">
              Import Methods
            </h4>
            <p className="text-xs text-gray-500">
              Choose how you want to add students to your class
            </p>

            <ul className="space-y-3 mt-2">
              <li
                className={`rounded-md p-4 border ${
                  selectedMethod === "file"
                    ? "bg-indigo-50 border-indigo-200"
                    : "border-gray-200"
                }`}
              >
                <label className="flex items-start cursor-pointer">
                  <input
                    type="radio"
                    name="importMethod"
                    checked={selectedMethod === "file"}
                    onChange={() => setSelectedMethod("file")}
                    className="h-4 w-4 text-indigo-600 mt-1"
                  />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">
                      Upload Spreadsheet
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Upload an Excel, CSV, or Google Sheets file. Column
                      First Name is required; Last Name and Email are optional.
                    </p>

                    {selectedMethod === "file" && (
                      <div className="mt-3">
                        <label
                          htmlFor="dropzone-file"
                          onDrop={(e) => {
                            e.preventDefault();
                            if (e.dataTransfer.files[0]) {
                              setFile(e.dataTransfer.files[0]);
                            }
                          }}
                          onDragOver={(e) => e.preventDefault()}
                          className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-white hover:bg-gray-50"
                        >
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <svg
                              className="w-8 h-8 mb-3 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                              />
                            </svg>
                            <p className="mb-1 text-sm text-gray-500">
                              {file?.name || "Drag & drop or click to browse"}
                            </p>
                            <p className="text-xs text-gray-500">
                              Excel, CSV, Google Sheets
                            </p>
                          </div>
                          <input
                            id="dropzone-file"
                            type="file"
                            onChange={handleFileChange}
                            className="hidden"
                            accept=".csv,.xls,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          />
                        </label>
                        <div className="mt-2">
                          <a
                            href="#"
                            className="text-xs text-indigo-600 hover:text-indigo-900"
                          >
                            Download template spreadsheet
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              </li>

              <li className="border border-gray-200 rounded-md p-4">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="radio"
                    name="importMethod"
                    checked={selectedMethod === "manual"}
                    onChange={() => setSelectedMethod("manual")}
                    className="h-4 w-4 text-indigo-600 mt-1"
                  />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">
                      Manual Entry
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Add students one by one. Only the first name is needed.
                      Students without an email sign in with a class code and a
                      picture PIN (print their login cards afterwards).
                    </p>
                  </div>
                </label>
              </li>
            </ul>

            {selectedMethod === "manual" && (
              <div className="mt-4 space-y-3 max-h-[300px] overflow-y-auto">
                {students.map((student, index) => (
                  <div key={index} className="grid grid-cols-3 gap-2">
                    <input
                      type="text"
                      placeholder="First Name"
                      value={student.firstName}
                      onChange={(e) => {
                        const updated = [...students];
                        updated[index] = {
                          ...student,
                          firstName: e.target.value,
                        };
                        setStudents(updated);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <input
                      type="text"
                      placeholder="Last Name (optional)"
                      value={student.lastName}
                      onChange={(e) => {
                        const updated = [...students];
                        updated[index] = {
                          ...student,
                          lastName: e.target.value,
                        };
                        setStudents(updated);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <input
                      type="email"
                      placeholder="Email (optional)"
                      value={student.email}
                      onChange={(e) => {
                        const updated = [...students];
                        updated[index] = { ...student, email: e.target.value };
                        setStudents(updated);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setStudents([
                      ...students,
                      { firstName: "", lastName: "", email: "" },
                    ])
                  }
                  className="text-sm text-indigo-600 hover:text-indigo-800"
                >
                  + Add one more
                </button>
              </div>
            )}
          </div>

          {/* Select Class */}
          {!selectedClassId && (
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Select Class to Import Into
              </h4>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {classes.map((cls) => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 flex justify-end space-x-3">
            <button onClick={onClose} className="btn btn-small btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={uploading || (selectedClassId && loadingClass)}
              className="btn btn-small"
            >
              {uploading ? "Importing..." : "Import Students"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
